// src/rooms.ts
// Community rooms: posts (laid out like the PenPal Board) with comments, in themed rooms.
//
// Data (checked by firestore.rules):
// - roomThreads/{threadId}: a post: room, title, body (rich-text HTML), color,
//   categories, cw?, anonymous, uid?, username, createdAt, lastReplyAt, removed?
// - roomThreads/{threadId}/replies/{replyId}: a comment (plain text): body, cw?,
//   anonymous, uid?, username, createdAt, removed?
// - roomAuthors/{threadId} or {threadId_replyId}: who wrote an anonymous post.
//   Only the author can read it (moderators see it in the Firebase console).
// - users/{uid}/private/rooms: your own threads and replies, so you can
//   find, delete, or download them (including anonymous ones).
import { useEffect, useState } from "react";
import {
  Timestamp,
  arrayUnion,
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import { checkPace, markPace } from "./pace";

export type Room = {
  id: string;
  title: string;
  description: string;
  topics: string[];
  // optional wording for the room's post form
  titlePlaceholder?: string;
  bodyPlaceholder?: string;
  prompts?: string[];
};

export const ROOMS: Room[] = [
  {
    id: "introductions",
    title: "Introductions",
    description:
      "New here? Come say hi! Tell us a little about yourself, and welcome the other new folks too.",
    titlePlaceholder: "e.g. Hi! I'm a sleepy poet who loves rainy days",
    bodyPlaceholder:
      "Tell us a little about you: what you love, what you're curious about, and what kind of pen pal or friend you're hoping to find. Share as much or as little as feels good.",
    prompts: [
      "Start with three things you love and one thing you're curious about.",
      "What name and pronouns would you like folks to use?",
      "What brought you to PenPal Board?",
      "What kind of pen pal or friend are you hoping to find?",
      "How often do you like to write? Fast replies, slow letters, whenever spoons allow?",
      "Share a song, a snack, and a place that feel like you.",
      "What's something you could talk about for hours?",
      "What's a little thing that makes your day better?",
      "Describe yourself the way a good friend would.",
      "What would you love to talk about in your first few letters?",
      "What does community mean to you?",
      "What's one fun fact about you?",
    ],
    topics: [
      "New Here",
      "Looking for Pen Pals",
      "Just Saying Hi",
      "Slow Writer",
      "Frequent Writer",
      "Writer & Poet",
      "Artist",
      "Reader",
      "Gamer",
      "Crafter",
      "Music Lover",
      "Plant Parent",
      "Pet Parent",
      "Nature Lover",
      "Night Owl",
      "Early Bird",
      "Introvert",
      "Extrovert",
      "Trans",
      "Nonbinary",
      "Two-Spirit",
      "QTBIPOC",
      "Disabled",
      "Chronically Ill",
      "Neurodivergent",
      "Spoonie",
      "Elder",
      "Student",
      "Parent",
      "Sober",
    ],
  },
  {
    id: "support",
    topics: [
      "Neurodivergence",
      "Grief",
      "Trauma & CPTSD",
      "Mental Health",
      "Chronic Illness",
      "Community Care",
      "Need to Vent",
      "Advice Wanted",
      "Anxiety",
      "Depression",
      "Burnout",
      "Loneliness",
      "Gender Dysphoria",
      "Coming Out",
      "Family Estrangement",
      "Relationships & Breakups",
      "Self-Harm Recovery",
      "Eating Disorder Recovery",
      "Sobriety & Recovery",
      "Medical Trauma",
      "Disability & Access",
      "Therapy & Meds",
      "Housing & Money Stress",
      "Work & School Stress",
      "Sleep Struggles",
      "Body Image",
      "Self-Worth",
      "Just Need Comfort",
      "Wins Worth Celebrating",
      "Resources & Hotlines",
    ],
    title: "Support & Connection",
    description:
      "Tender spaces for neurodivergence, grief, trauma, CPTSD, and building community care. Come as you are.",
  },
  {
    id: "hobbies",
    topics: [
      "Art",
      "Books",
      "Crafts",
      "Gaming",
      "Music",
      "Nature",
      "Writing",
      "Cooking",
      "Weird Little Joys",
      "Drawing & Painting",
      "Knitting & Crochet",
      "Sewing & Fashion",
      "Embroidery",
      "Zines",
      "Journaling",
      "Poetry",
      "Photography",
      "Film & TV",
      "Anime & Manga",
      "Tabletop & D&D",
      "Cosplay",
      "Gardening & Plants",
      "Baking",
      "Birdwatching",
      "Hiking & Outdoors",
      "Collecting",
      "Puzzles & Games",
      "Dance",
      "Fandom",
      "Pets & Critters",
      "Learning Something New",
    ],
    title: "Hobbies & Self-Expression",
    description:
      "A fun and affirming space to share your art, books, crafts, gaming, nature pics, and weird little joys.",
  },
  {
    id: "culture",
    topics: [
      "Language",
      "Roots & Heritage",
      "Faith & Spirituality",
      "Indigenous & Two-Spirit",
      "Diaspora",
      "Food & Traditions",
      "Curious Questions",
      "Ancestors & Family History",
      "Black Queer Joy",
      "Latinx & Latine",
      "Asian & Pacific Islander",
      "Middle Eastern & North African",
      "Jewish Life",
      "Muslim Life",
      "Christian & Queer",
      "Pagan & Witchy",
      "Buddhism & Meditation",
      "Leaving Religion",
      "Mixed & Multiracial",
      "Immigration & Belonging",
      "Holidays & Celebrations",
      "Music & Dance Traditions",
      "Storytelling & Folklore",
      "Rural & Small Town",
      "Neurodivergent Culture",
      "Deaf Culture",
      "Queer History",
    ],
    title: "Culture, Faith & Identity",
    description:
      "Hold space for your roots. Explore language, culture, and spiritual journeys in a gentle and curious space.",
  },
  {
    id: "penpal-life",
    topics: [
      "Letter Prompts",
      "Stickers & Stationery",
      "Mail Art",
      "Cute Replies",
      "Snail Mail Tips",
      "Pen Pal Wins",
      "Finding a Pen Pal",
      "First Letter Nerves",
      "Handwriting",
      "Envelope Art",
      "Wax Seals",
      "Stamps & Postage",
      "Washi Tape",
      "Postcards",
      "Journaling Spreads",
      "Gift Swaps",
      "Care Packages",
      "International Mail",
      "Digital Letters",
      "Letter Etiquette",
      "Slow Replies & Gentleness",
      "Keeping in Touch",
      "Boundaries with Pen Pals",
      "Questions to Ask",
      "Favorite Pens",
      "Paper & Notebooks",
      "Mail Day Joy",
    ],
    title: "Pen Pal Life",
    description:
      "Drop your letter prompts, sticker recs, mail art tips, and post about cute replies you've received!",
  },
  {
    id: "liberation",
    topics: [
      "Trans Rights",
      "Anti-Racism",
      "Disability Justice",
      "Mutual Aid",
      "Climate",
      "Organizing",
      "Rage Corner",
      "Know Your Rights",
      "Healthcare Access",
      "Gender-Affirming Care",
      "Indigenous Sovereignty",
      "Palestine",
      "Prison Abolition",
      "Housing Justice",
      "Workers' Rights",
      "AI Policy & Reform",
      "Reproductive Justice",
      "Immigrant Justice",
      "Fat Liberation",
      "Sex Worker Rights",
      "Voting & Elections",
      "Protest Safety",
      "Community Safety",
      "Calls to Action",
      "Good News for the Movement",
      "Rest as Resistance",
      "Queer History & Pride",
    ],
    title: "Liberation & Justice",
    description:
      "Chat about trans rights, anti-racism, mutual aid, disability justice, climate resistance, or rage. :3",
  },
  {
    id: "chill",
    topics: [
      "Vibe Check",
      "Grounding Tools",
      "Good News",
      "Pet Talk",
      "Daily Check-In",
      "Random Thoughts",
      "Small Wins",
      "Gratitude",
      "What I'm Eating",
      "What I'm Reading",
      "What I'm Watching",
      "What I'm Listening To",
      "Cozy Things",
      "Self-Care Ideas",
      "Rest Day",
      "Spoons Today",
      "Weather Report",
      "Silly Questions",
      "Would You Rather",
      "Memes & Jokes",
      "Dreams Last Night",
      "Weekend Plans",
      "Shower Thoughts",
      "Soft Hellos",
      "Body Doubling",
      "Tea & Coffee Corner",
    ],
    title: "Chill & Check-Ins",
    description:
      "No pressure. Just hang out. Share a vibe check, a grounding tool, or how your day is really going.",
  },
];

export const roomById = (id?: string) => ROOMS.find((r) => r.id === id);

export const LIMITS = { title: 120, body: 20000, reply: 2000, cw: 100 };

export type Post = {
  id: string;
  body: string;
  cw?: string;
  anonymous?: boolean;
  uid?: string;
  username: string;
  createdAt?: Timestamp;
  removed?: boolean;
  hidden?: boolean; // hidden by a circle's quartermaster or first mate
};
// A room post: like a board postcard (rich-text body, paper color, topics).
export type Thread = Post & {
  room: string;
  title: string;
  color?: string;
  categories?: string[];
  lastReplyAt?: Timestamp;
};

export const threadRef = (id: string) => doc(db, "roomThreads", id);
export const repliesCol = (threadId: string) => collection(db, "roomThreads", threadId, "replies");
const authorRef = (key: string) => doc(db, "roomAuthors", key);
const mineRef = (uid: string) => doc(db, "users", uid, "private", "rooms");

// Who a post says it's from. Anonymous posts store no uid on the post itself.
function byline(username: string, anonymous: boolean) {
  return anonymous ? { anonymous: true, username: "anonymous" } : { anonymous: false, username };
}

export async function startThread(
  uid: string,
  username: string,
  room: string,
  p: { subject: string; message: string; color: string; categories: string[]; cw: string; anonymous: boolean }
) {
  const { anonymous, cw } = p;
  const ref = doc(collection(db, "roomThreads"));
  await checkPace(uid, "post");
  const batch = writeBatch(db);
  markPace(batch, uid, "post");
  if (anonymous) batch.set(authorRef(ref.id), { uid });
  batch.set(ref, {
    room,
    title: p.subject.trim().slice(0, LIMITS.title),
    body: p.message.slice(0, LIMITS.body), // rich-text HTML, sanitized when shown
    color: p.color,
    categories: p.categories.slice(0, 3),
    ...(cw.trim() ? { cw: cw.trim().slice(0, LIMITS.cw) } : {}),
    ...byline(username, anonymous),
    ...(anonymous ? {} : { uid }),
    removed: false,
    createdAt: serverTimestamp(),
    lastReplyAt: serverTimestamp(),
  });
  await batch.commit();
  await setDoc(mineRef(uid), { threads: arrayUnion(ref.id) }, { merge: true });
  markSeen(ref.id);
  return ref.id;
}

export async function replyTo(
  uid: string,
  username: string,
  threadId: string,
  body: string,
  cw: string,
  anonymous: boolean
) {
  const ref = doc(repliesCol(threadId));
  await checkPace(uid, "comment");
  const batch = writeBatch(db);
  markPace(batch, uid, "comment");
  if (anonymous) batch.set(authorRef(`${threadId}_${ref.id}`), { uid });
  batch.set(ref, {
    body: body.trim().slice(0, LIMITS.reply),
    ...(cw.trim() ? { cw: cw.trim().slice(0, LIMITS.cw) } : {}),
    ...byline(username, anonymous),
    ...(anonymous ? {} : { uid }),
    removed: false,
    createdAt: serverTimestamp(),
  });
  batch.update(threadRef(threadId), { lastReplyAt: serverTimestamp() });
  await batch.commit();
  await setDoc(mineRef(uid), { replies: arrayUnion(`${threadId}_${ref.id}`) }, { merge: true });
  markSeen(threadId);
}

// Taking back your own post leaves a "removed" placeholder, so replies to it
// still make sense.
export async function removeThread(threadId: string) {
  await updateDoc(threadRef(threadId), { removed: true, title: "(removed)", body: "", cw: "" });
}
export async function removeReply(threadId: string, replyId: string) {
  await updateDoc(doc(repliesCol(threadId), replyId), { removed: true, body: "", cw: "" });
}

// Is this (possibly anonymous) post mine?
export async function isMine(uid: string, post: Post, key: string) {
  if (post.uid) return post.uid === uid;
  if (!post.anonymous) return false;
  const snap = await getDoc(authorRef(key)).catch(() => null);
  return !!snap?.exists() && snap.data()?.uid === uid;
}

// ---------- "new replies" for threads you started ----------

const SEEN_KEY = "penpal-room-seen";

function loadSeen(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}");
  } catch {
    return {};
  }
}

export function markSeen(threadId: string) {
  try {
    const seen = loadSeen();
    seen[threadId] = Date.now() + 5000; // a little slack for the server clock
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    window.dispatchEvent(new Event(SEEN_KEY));
  } catch {
    // storage unavailable; the badge just won't clear on this device
  }
}

// How many of your threads have replies you haven't seen yet.
export function useNewRoomReplies(uid?: string): number {
  const [ids, setIds] = useState<string[]>([]);
  const [latest, setLatest] = useState<Record<string, number>>({});
  const [seenTick, setSeenTick] = useState(0);

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(
      mineRef(uid),
      (snap) => setIds(((snap.data()?.threads as string[]) || []).slice(-20)),
      () => setIds([])
    );
  }, [uid]);

  useEffect(() => {
    const unsubs = ids.map((id) =>
      onSnapshot(
        threadRef(id),
        (snap) =>
          setLatest((prev) => ({ ...prev, [id]: snap.data()?.lastReplyAt?.toMillis?.() ?? 0 })),
        () => undefined
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [ids]);

  useEffect(() => {
    const bump = () => setSeenTick((t) => t + 1);
    window.addEventListener(SEEN_KEY, bump);
    return () => window.removeEventListener(SEEN_KEY, bump);
  }, []);

  const seen = loadSeen();
  void seenTick;
  return ids.filter((id) => (latest[id] || 0) > (seen[id] || 0)).length;
}
